import { supabase } from '@/lib/supabase/client';

// Temporary: Disable security monitoring for debugging
const MONITORING_ENABLED = false;

interface SecurityEvent {
  event_type: string;
  event_category: 'authentication' | 'authorization' | 'data_access' | 'system';
  user_id?: string;
  tenant_id?: string;
  token_id?: string; // Changed from session_id (Supabase manages sessions internally)
  resource_type?: string;
  resource_id?: string;
  action?: string;
  outcome?: 'success' | 'failure' | 'blocked';
  risk_score?: number;
  ip_address?: string;
  user_agent?: string;
  metadata?: Record<string, any>;
}

// In-memory event queue for batching
const eventQueue: SecurityEvent[] = [];
let flushTimer: NodeJS.Timeout | null = null;

// Configuration
const BATCH_SIZE = 50;
const FLUSH_INTERVAL = 5000; // 5 seconds
const MAX_QUEUE_SIZE = 1000;

/**
 * Log a security event
 */
export function logSecurityEvent(
  eventType: string,
  metadata?: Record<string, any>,
  options?: Partial<SecurityEvent>
): void {
  // Skip if monitoring disabled
  if (!MONITORING_ENABLED) {
    return;
  }
  try {
    // Get current user and session info
    const session = supabase.auth.session;
    const userId = session?.user?.id;

    // Parse JWT for additional context
    let tenantId: string | undefined;
    let tokenId: string | undefined;

    if (session?.access_token) {
      try {
        const payload = session.access_token.split('.')[1];
        const claims = JSON.parse(atob(payload));
        tenantId = claims.tenant_id;
        tokenId = claims.token_id; // Changed from session_id to token_id
      } catch (e) {
        // Silent fail - don't break security logging
      }
    }

    // Determine event category
    const category = determineEventCategory(eventType);

    // Calculate risk score
    const riskScore = calculateRiskScore(eventType, metadata);

    // Create event object
    const event: SecurityEvent = {
      event_type: eventType,
      event_category: category,
      user_id: userId,
      tenant_id: tenantId,
      token_id: tokenId, // Changed from session_id to token_id
      outcome: 'success',
      risk_score: riskScore,
      metadata: {
        ...metadata,
        timestamp: new Date().toISOString(),
        client_time: Date.now(),
        page_url: window.location.href,
        referrer: document.referrer
      },
      ...options
    };

    // Add to queue
    addToQueue(event);

    // Log high-risk events immediately
    if (riskScore >= 80) {
      flushEvents(true);
    }
  } catch (error) {
    console.error('Failed to log security event:', error);
  }
}

/**
 * Add event to queue and manage flushing
 */
function addToQueue(event: SecurityEvent): void {
  // Prevent queue overflow
  if (eventQueue.length >= MAX_QUEUE_SIZE) {
    eventQueue.shift(); // Remove oldest event
  }

  eventQueue.push(event);

  // Start or reset flush timer
  if (flushTimer) {
    clearTimeout(flushTimer);
  }

  flushTimer = setTimeout(() => flushEvents(), FLUSH_INTERVAL);

  // Flush if batch size reached
  if (eventQueue.length >= BATCH_SIZE) {
    flushEvents();
  }
}

/**
 * Flush events to backend
 */
async function flushEvents(immediate = false): Promise<void> {
  if (!immediate && eventQueue.length === 0) return;

  // Clear timer
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  // Get events to send
  const eventsToSend = [...eventQueue];
  eventQueue.length = 0; // Clear queue

  if (eventsToSend.length === 0) return;

  try {
    // Send to backend via edge function
    const { error } = await supabase.functions.invoke('log-security-events', {
      body: { events: eventsToSend }
    });

    if (error) {
      console.error('Failed to send security events:', error);
      // Re-queue events on failure (with limit)
      if (eventQueue.length < MAX_QUEUE_SIZE / 2) {
        eventQueue.unshift(...eventsToSend.slice(0, 10)); // Re-queue up to 10 events
      }
    }
  } catch (error) {
    console.error('Error flushing security events:', error);
  }
}

/**
 * Determine event category from event type
 */
function determineEventCategory(eventType: string): SecurityEvent['event_category'] {
  if (eventType.includes('auth') || eventType.includes('login') || eventType.includes('jwt')) {
    return 'authentication';
  }
  if (
    eventType.includes('permission') ||
    eventType.includes('role') ||
    eventType.includes('access')
  ) {
    return 'authorization';
  }
  if (eventType.includes('data') || eventType.includes('query') || eventType.includes('fetch')) {
    return 'data_access';
  }
  return 'system';
}

/**
 * Calculate risk score based on event type and metadata
 */
function calculateRiskScore(eventType: string, metadata?: Record<string, any>): number {
  let score = 0;

  // High-risk event types
  const highRiskEvents = [
    'cross_tenant_attempt',
    'unauthorized_tenant_switch',
    'jwt_tampering',
    'service_role_exposure',
    'sql_injection_attempt'
  ];

  const mediumRiskEvents = [
    'failed_permission_check',
    'jwt_expired',
    'invalid_tenant_context',
    'api_rate_limit_exceeded'
  ];

  if (highRiskEvents.includes(eventType)) {
    score = 80;
  } else if (mediumRiskEvents.includes(eventType)) {
    score = 50;
  } else if (eventType.includes('error') || eventType.includes('fail')) {
    score = 30;
  } else {
    score = 10;
  }

  // Adjust based on metadata
  if (metadata?.repeated_attempts && metadata.repeated_attempts > 5) {
    score += 20;
  }

  if (metadata?.cross_tenant) {
    score += 30;
  }

  return Math.min(score, 100);
}

/**
 * Security monitoring for suspicious patterns
 */
class SecurityMonitor {
  private failedAttempts: Map<string, number> = new Map();
  private lastReset: number = Date.now();
  private readonly RESET_INTERVAL = 60 * 60 * 1000; // 1 hour
  private readonly THRESHOLD_MULTIPLIER = {
    failed_auth: 5,
    failed_permission: 10,
    cross_tenant: 1,
    rapid_requests: 100
  };

  constructor() {
    // Reset counters periodically
    setInterval(() => this.resetCounters(), this.RESET_INTERVAL);
  }

  /**
   * Track failed attempt
   */
  trackFailedAttempt(userId: string, attemptType: keyof typeof this.THRESHOLD_MULTIPLIER): void {
    const key = `${userId}:${attemptType}`;
    const current = this.failedAttempts.get(key) || 0;
    this.failedAttempts.set(key, current + 1);

    const threshold = this.THRESHOLD_MULTIPLIER[attemptType];
    if (current + 1 >= threshold) {
      logSecurityEvent(
        'suspicious_activity_threshold',
        {
          userId,
          attemptType,
          count: current + 1,
          threshold
        },
        {
          risk_score: 90,
          outcome: 'blocked'
        }
      );

      // Auto-block after threshold
      this.blockUser(userId, attemptType);
    }
  }

  /**
   * Block user temporarily
   */
  private async blockUser(userId: string, reason: string): Promise<void> {
    try {
      await supabase.functions.invoke('block-user-temporarily', {
        body: {
          userId,
          reason,
          duration: 3600 // 1 hour
        }
      });
    } catch (error) {
      console.error('Failed to block user:', error);
    }
  }

  /**
   * Reset counters
   */
  private resetCounters(): void {
    const now = Date.now();
    if (now - this.lastReset >= this.RESET_INTERVAL) {
      this.failedAttempts.clear();
      this.lastReset = now;
    }
  }

  /**
   * Check if user is currently blocked
   */
  async isUserBlocked(userId: string): Promise<boolean> {
    try {
      const { data } = await supabase.functions.invoke('check-user-block-status', {
        body: { userId }
      });
      return data?.blocked || false;
    } catch (error) {
      console.error('Failed to check block status:', error);
      return false;
    }
  }
}

// Export singleton instance
export const securityMonitor = new SecurityMonitor();

/**
 * Flush events on page unload
 */
window.addEventListener('beforeunload', () => {
  flushEvents(true);
});

/**
 * Security event types for type safety
 */
export const SecurityEventTypes = {
  // Authentication
  LOGIN_SUCCESS: 'login_success',
  LOGIN_FAILURE: 'login_failure',
  LOGOUT: 'logout',
  JWT_EXPIRED: 'jwt_expired',
  JWT_INVALID: 'jwt_invalid',
  JWT_REFRESH: 'jwt_refresh',

  // Authorization
  PERMISSION_GRANTED: 'permission_granted',
  PERMISSION_DENIED: 'permission_denied',
  ROLE_CHECK_FAILED: 'role_check_failed',

  // Tenant
  TENANT_SWITCH: 'tenant_switch',
  CROSS_TENANT_ATTEMPT: 'cross_tenant_attempt',
  TENANT_CONTEXT_MISMATCH: 'tenant_context_mismatch',

  // Data Access
  DATA_ACCESS: 'data_access',
  DATA_MODIFICATION: 'data_modification',
  SENSITIVE_DATA_ACCESS: 'sensitive_data_access',

  // System
  API_ERROR: 'api_error',
  RATE_LIMIT_EXCEEDED: 'rate_limit_exceeded',
  SUSPICIOUS_ACTIVITY: 'suspicious_activity'
} as const;
